import { Button } from "@mui/material";
import { SearchParams, SearchResponse } from "meilisearch";
import ContentWithModal from "./components/contentWithModal";
import DownloadIcon from "./components/icons/download";
import ModalContent from "./components/modalContent";
import ProductModel from "./components/productModel";
import { categories, displayedColumns } from "./constants";
import { ContentWithModalItemType, Link } from "./types";

const createConfigurableTestSystemsData = (
	image: string,
	productModel: string,
	productName: string,
	productDescription: string,
	typicalTestApplications: ContentWithModalItemType[],
	brochureLink?: Link
) => {
	return [
		<ProductModel
			image={image}
			productModel={productModel}
			modalContent={
				!!productModel ||
				!!productName ||
				!!productDescription ||
				!!typicalTestApplications ? (
					<ModalContent
						title={productModel}
						subtitle={productName}
						image={image}
						content={
							<div className="flex flex-col gap-[25px] max-w-[500px]">
								<div
									dangerouslySetInnerHTML={{
										__html: productDescription,
									}}
								/>
								<div className="flex text-sm gap-4">
									<div className="flex flex-col flex-1 gap-[10px]">
										<p className="font-semibold">
											Typical Applications
										</p>
										<div
											className="whitespace-pre-line flex flex-col"
											dangerouslySetInnerHTML={{
												__html: typicalTestApplications
													.map((item) => item.content)
													.join(""),
											}}
										/>
									</div>
								</div>
							</div>
						}
						button={
							!!brochureLink ? (
								<BrochureDownloadButton
									brochureLink={brochureLink}
								/>
							) : undefined
						}
					/>
				) : undefined
			}
		/>,
		<div style={{ padding: "10px" }}>{productName}</div>,
		<ContentWithModal contentArray={typicalTestApplications} />,
	];
};

const createParallelDigitalTestData = (
	image: string,
	productModel: string,
	productName: string,
	productDescription: string,
	supportedBus: ContentWithModalItemType[],
	capabilities: ContentWithModalItemType[],
	brochureLink?: Link
) => {
	return [
		<ProductModel
			image={image}
			productModel={productModel}
			modalContent={
				!!productModel ||
				!!productName ||
				!!productDescription ||
				!!supportedBus ||
				!!capabilities ? (
					<ModalContent
						title={productModel}
						subtitle={productName}
						image={image}
						content={
							!!productDescription ||
							!!supportedBus ||
							!!capabilities ? (
								<div className="flex flex-col gap-[25px] max-w-[500px]">
									<div
										dangerouslySetInnerHTML={{
											__html: productDescription,
										}}
									/>
									<div className="flex text-sm gap-4">
										<div className="flex flex-col flex-1 gap-[10px]">
											<p className="font-semibold">
												Supported Buses
											</p>
											<div
												dangerouslySetInnerHTML={{
													__html: supportedBus
														.map(
															(item) =>
																item.content
														)
														.join(""),
												}}
											/>
										</div>
										<div className="flex flex-col flex-1 gap-[10px]">
											<p className="font-semibold">
												Capabilities
											</p>
											<div
												dangerouslySetInnerHTML={{
													__html: capabilities
														.map(
															(item) =>
																item.content
														)
														.join(""),
												}}
											/>
										</div>
									</div>
								</div>
							) : undefined
						}
						button={
							!!brochureLink ? (
								<BrochureDownloadButton
									brochureLink={brochureLink}
								/>
							) : undefined
						}
					/>
				) : undefined
			}
		/>,
		<div style={{ padding: "10px" }}>{productName}</div>,
		<ContentWithModal contentArray={supportedBus} />,
		<ContentWithModal contentArray={capabilities} />,
	];
};

const createSerialBusTestData = (
	image: string,
	productModel: string,
	productName: string,
	productDescription: string,
	supportedBus: ContentWithModalItemType[],
	typicalTestApplications: ContentWithModalItemType[],
	brochureLink?: Link
) => {
	return [
		<ProductModel
			image={image}
			productModel={productModel}
			modalContent={
				!!productModel ||
				!!productName ||
				!!productDescription ||
				!!supportedBus ||
				!!typicalTestApplications ? (
					<ModalContent
						title={productModel}
						subtitle={productName}
						image={image}
						content={
							<div className="flex flex-col gap-[25px] max-w-[500px]">
								<div
									dangerouslySetInnerHTML={{
										__html: productDescription,
									}}
								/>
								<div className="flex text-sm gap-4">
									<div className="flex flex-col flex-1 gap-[10px]">
										<p className="font-semibold">
											Typical Applications
										</p>
										<div
											className="whitespace-pre-line flex flex-col"
											dangerouslySetInnerHTML={{
												__html: typicalTestApplications
													.map((item) => item.content)
													.join(""),
											}}
										/>
									</div>
									<div className="flex flex-col flex-1 gap-[10px]">
										<p className="font-semibold">
											Supported Buses
										</p>
										<div
											className="whitespace-pre-line flex flex-col"
											dangerouslySetInnerHTML={{
												__html: supportedBus
													.map((item) => item.content)
													.join(""),
											}}
										/>
									</div>
								</div>
							</div>
						}
						button={
							!!brochureLink ? (
								<BrochureDownloadButton
									brochureLink={brochureLink}
								/>
							) : undefined
						}
					/>
				) : undefined
			}
		/>,
		<div style={{ padding: "10px" }}>{productName}</div>,
		<ContentWithModal contentArray={supportedBus} />,
		<ContentWithModal contentArray={typicalTestApplications} />,
	];
};

const createOpticalTestData = (
	image: string,
	productModel: string,
	productName: string,
	productDescription: string,
	capabilities: ContentWithModalItemType[],
	brochureLink?: Link
) => {
	return [
		<ProductModel
			image={image}
			productModel={productModel}
			modalContent={
				!!productModel ||
				!!productName ||
				!!productDescription ||
				!!capabilities ? (
					<ModalContent
						title={productModel}
						subtitle={productName}
						image={image}
						content={
							<div className="flex flex-col gap-[25px] max-w-[500px]">
								<div
									dangerouslySetInnerHTML={{
										__html: productDescription,
									}}
								/>
								<div className="flex text-sm gap-4">
									<div className="flex flex-col flex-1 gap-[10px]">
										<p className="font-semibold">
											Capabilities
										</p>
										<div
											className="whitespace-pre-line flex flex-col"
											dangerouslySetInnerHTML={{
												__html: capabilities
													.map((item) => item.content)
													.join(""),
											}}
										/>
									</div>
								</div>
							</div>
						}
						button={
							!!brochureLink ? (
								<BrochureDownloadButton
									brochureLink={brochureLink}
								/>
							) : undefined
						}
					/>
				) : undefined
			}
		/>,
		<div style={{ padding: "10px" }}>{productName}</div>,
		<ContentWithModal contentArray={capabilities} />,
	];
};

const createSwitchingData = (
	image: string,
	productModel: string,
	productName: string,
	productDescription: string,
	capabilities: ContentWithModalItemType[],
	brochureLink?: Link
) => {
	return [
		<ProductModel
			image={image}
			productModel={productModel}
			modalContent={
				!!productModel ||
				!!productName ||
				!!productDescription ||
				!!capabilities ? (
					<ModalContent
						title={productModel}
						subtitle={productName}
						image={image}
						content={
							<div className="flex flex-col gap-[25px] max-w-[500px]">
								<div
									dangerouslySetInnerHTML={{
										__html: productDescription,
									}}
								/>
								<div className="flex text-sm gap-4">
									<div className="flex flex-col flex-1 gap-[10px]">
										<p className="font-semibold">
											Capabilities
										</p>
										<div
											className="whitespace-pre-line flex flex-col"
											dangerouslySetInnerHTML={{
												__html: capabilities
													.map((item) => item.content)
													.join(""),
											}}
										/>
									</div>
								</div>
							</div>
						}
						button={
							!!brochureLink ? (
								<BrochureDownloadButton
									brochureLink={brochureLink}
								/>
							) : undefined
						}
					/>
				) : undefined
			}
		/>,
		<div style={{ padding: "10px" }}>{productName}</div>,
		<ContentWithModal contentArray={capabilities} />,
	];
};

const createAnalogTestData = (
	image: string,
	productModel: string,
	productName: string,
	productDescription: string,
	capabilities: string,
	brochureLink?: Link
) => {
	return [
		<ProductModel
			image={image}
			productModel={productModel}
			modalContent={
				!!productModel ||
				!!productName ||
				!!productDescription ||
				!!capabilities ? (
					<ModalContent
						title={productModel}
						subtitle={productName}
						image={image}
						content={
							<div className="flex flex-col gap-[25px] max-w-[500px]">
								<div
									dangerouslySetInnerHTML={{
										__html: productDescription,
									}}
								/>
								<div className="flex text-sm gap-4">
									<div className="flex flex-col flex-1 gap-[10px]">
										<p className="font-semibold">
											Capabilities
										</p>
										<div
											dangerouslySetInnerHTML={{
												__html: capabilities,
											}}
										/>
									</div>
								</div>
							</div>
						}
						button={
							!!brochureLink ? (
								<BrochureDownloadButton
									brochureLink={brochureLink}
								/>
							) : undefined
						}
					/>
				) : undefined
			}
		/>,
		<div
			style={{ padding: "10px" }}
			dangerouslySetInnerHTML={{ __html: productName }}
		/>,
		<div
			style={{ padding: "10px" }}
			dangerouslySetInnerHTML={{ __html: productDescription }}
		/>,
	];
};

export const mapSearchResults = (
	category: keyof typeof displayedColumns,
	results: SearchResponse<Record<string, any>, SearchParams>
) => {
	const headers = displayedColumns[category] || [];
	if (category === categories.CONFIGURABLE_TEST_SYSTEMS)
		return {
			headers,
			rows: results.hits.map((hit: any) =>
				createConfigurableTestSystemsData(
					hit?.image,
					hit?.model,
					hit?.name,
					hit?.description,
					hit?.typical_test_applications || [],
					hit?.brochure
				)
			),
		};
	else if (category === categories.PARALLEL_DIGITAL_TEST)
		return {
			headers,
			rows: results.hits.map((hit: any) =>
				createParallelDigitalTestData(
					hit?.image,
					hit?.model,
					hit?.name,
					hit?.description,
					hit?.supported_bus_table || [],
					hit?.capabilities || [],
					hit?.brochure
				)
			),
		};
	else if (category === categories.SERIAL_BUS_TEST)
		return {
			headers,
			rows: results.hits.map((hit: any) =>
				createSerialBusTestData(
					hit?.image,
					hit?.model,
					hit?.name,
					hit?.description,
					hit?.supported_bus_table || [],
					hit?.typical_test_applications || [],
					hit?.brochure
				)
			),
		};
	else if (category === categories.OPTICAL_TEST)
		return {
			headers,
			rows: results.hits.map((hit: any) =>
				createOpticalTestData(
					hit?.image,
					hit?.model,
					hit?.name,
					hit?.description,
					hit?.capabilities || [],
					hit?.brochure
				)
			),
		};
	else if (category === categories.SWITCHING)
		return {
			headers,
			rows: results.hits.map((hit: any) =>
				createSwitchingData(
					hit?.image,
					hit?.model,
					hit?.name,
					hit?.description,
					hit?.capabilities || [],
					hit?.brochure
				)
			),
		};
	else if (category === categories.ANALOG_TEST) {
		return {
			headers,
			rows: results.hits.map((hit: any) =>
				createAnalogTestData(
					hit?.image,
					hit?.model,
					hit?.name,
					hit?.description,
					hit?.capabilities,
					hit?.brochure
				)
			),
		};
	}
};

export { createAnalogTestData, createConfigurableTestSystemsData };

const BrochureDownloadButton = ({ brochureLink }: { brochureLink?: Link }) => {
	return (
		<Button
			startIcon={<DownloadIcon />}
			variant="contained"
			color="secondary"
			style={{ color: "white" }}
			onClick={() => window.open(brochureLink?.url, brochureLink?.target)}
		>
			Brochure
		</Button>
	);
};
